/**
 * Staff overseas-travel declaration helpers.
 *
 * The leave modal stays server-driven: it only asks the question when
 * calculate_leave_days returns overseas_travel_prompt.should_prompt.
 * These helpers keep payload sanitisation and inclusive calendar-day
 * counting out of the modal so they can be unit-tested.
 */

export const EMPTY_OVERSEAS_TRAVEL = {
  overseas_travel_declared: null,
  overseas_departure_date: '',
  overseas_return_date: '',
};

export const MIN_OVERSEAS_CONSECUTIVE_DAYS = 7;

export function parsePrompt(raw) {
  const prompt = raw || {};
  return {
    should_prompt: Boolean(prompt.should_prompt),
    opportunity_start: prompt.opportunity_start || null,
    opportunity_end: prompt.opportunity_end || null,
    opportunity_calendar_days: Number(prompt.opportunity_calendar_days) || 0,
  };
}

export function inclusiveCalendarDays(start, end) {
  if (!start) return 0;
  const startDate = start instanceof Date ? start : new Date(`${start}T00:00:00`);
  const endValue = end || start;
  const endDate = endValue instanceof Date ? endValue : new Date(`${endValue}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return 0;
  if (endDate < startDate) return 0;
  const ms = endDate.getTime() - startDate.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24)) + 1;
}

export function buildOverseasTravelPayload({
  shouldPrompt,
  declared,
  departure,
  returnDate,
} = {}) {
  if (!shouldPrompt) {
    return {
      overseas_travel_declared: null,
      overseas_departure_date: null,
      overseas_return_date: null,
    };
  }
  if (declared !== true) {
    return {
      overseas_travel_declared: declared === false ? false : null,
      overseas_departure_date: null,
      overseas_return_date: null,
    };
  }
  return {
    overseas_travel_declared: true,
    overseas_departure_date: departure || null,
    overseas_return_date: returnDate || null,
  };
}

export function clearedOverseasTravelState(current = {}) {
  if (
    current.overseas_travel_declared == null &&
    !current.overseas_departure_date &&
    !current.overseas_return_date
  ) {
    return current;
  }
  return { ...current, ...EMPTY_OVERSEAS_TRAVEL };
}

export const OVERSEAS_TRAVEL_FIELD_KEYS = [
  'overseas_travel_declared',
  'overseas_departure_date',
  'overseas_return_date',
];

export function validateOverseasTravelFields({
  shouldPrompt,
  declared,
  departure,
  returnDate,
} = {}) {
  const errors = {};
  if (!shouldPrompt) return errors;
  if (declared !== true && declared !== false) {
    errors.overseas_travel_declared =
      'Please confirm whether you will travel outside Singapore during this leave.';
    return errors;
  }
  if (declared !== true) return errors;
  if (!departure) {
    errors.overseas_departure_date = 'Date leaving Singapore is required.';
  }
  if (!returnDate) {
    errors.overseas_return_date = 'Date returning to Singapore is required.';
  }
  if (departure && returnDate && returnDate < departure) {
    errors.overseas_return_date =
      'Return date cannot be before the date you leave Singapore.';
  }
  return errors;
}

export function extractOverseasTravelServerErrors(errorData = {}) {
  const errors = {};
  OVERSEAS_TRAVEL_FIELD_KEYS.forEach((key) => {
    const raw = errorData[key];
    if (raw == null || raw === '') return;
    const message = Array.isArray(raw) ? raw[0] : raw;
    if (message) errors[key] = String(message);
  });
  return errors;
}

export function hasOverseasTravelFieldErrors(errors = {}) {
  return OVERSEAS_TRAVEL_FIELD_KEYS.some((key) => Boolean(errors[key]));
}

/**
 * When the employee taps Yes, seed departure/return from the server window
 * so they are not staring at two empty date fields. Existing values win.
 */
export function suggestedTravelDates(prompt = {}, current = {}) {
  return {
    departure: current.departure || prompt.opportunity_start || '',
    returnDate: current.returnDate || prompt.opportunity_end || '',
  };
}
